import dayjs, { Dayjs } from 'dayjs';

/**
 * Change the date format YYYY-MM-DD function
 * @param {any} value date string
 * @returns {string} format string
 */
export const changeDateFormat = (value: string) => {
  if (value === '' || value === undefined) return '';
  const date = new Date(value);
  // const dateMDY = new Intl.DateTimeFormat('en-US', {year: 'numeric', month: '2-digit',day: '2-digit'}).format(date);
  const dateMDY = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return dateMDY;
};
/**
 * Change the date format YYYY-MM-DD HH:mm function
 * @param {any} value date string
 * @returns {string} format string
 */
export const changeDateTimeFormat = (value: string) => {
  if (value === '' || value === undefined) return '';
  const date = new Date(value);
  // const dateMDY = new Intl.DateTimeFormat('en-US', {year: 'numeric', month: '2-digit',day: '2-digit'}).format(date);
  const dateMDY = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return dateMDY;
};

/**
 * Remove the empty keys in object function
 * @param {any} inputObject input object
 * @returns {object} updated object
 */
export const removeEmptyKeys = (inputObject: any) => {
  return Object.fromEntries(
    Object.entries(inputObject).filter(
      (value) => value[1] !== '' && value[1] !== null && value[1] !== undefined,
    ),
  );
};

/**
 * Split array
 * @param {any} array - The array
 * @param {any} chunkSize - The chunkSize
 */
export const splitArrayIntoChunks = (array: number[], chunkSize: number) => {
  const result = [];
  for (let index = 0; index < array.length; index += chunkSize) {
    result.push(array.slice(index, index + chunkSize));
  }
  return result;
};

/**
 * Convert the 12 hours to 24 hours
 * @param {any} time12Hour time 12 hour
 * @returns {string} time 24 hour
 */
export const convertTo24Hour = (time12Hour: string) => {
  const [time, modifier] = time12Hour.split(' ');
  const [hours, minutes] = time.split(':');

  let currentHours = parseInt(hours, 10);
  if (modifier === 'PM' && hours !== '12') {
    currentHours = parseInt(hours, 10) + 12;
  } else if (modifier === 'AM' && hours === '12') {
    currentHours = 0;
  }

  return `${String(currentHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Get the time format 24 to 12 hours
 * @param {any} time24 - The time24
 */
export const convertTo12HourFormat = (time24: string) => {
  const [hours, minutes] = time24.split(':');
  let currentHours = parseInt(hours, 10);

  const period = currentHours >= 12 ? 'PM' : 'AM';

  // Convert to 12-hour format
  currentHours = currentHours % 12 || 12; // Converts 0 to 12 for midnight

  return `${String(currentHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Convert UTC time string to 12-hour format with AM/PM based on timezone
 * @param {any} utcString - The utcString
 * @param {any} currentTimeZone - The currentTimeZone
 */
export const convertUtcTo12HourTime = (
  utcString: string,
  currentTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
) => {
  const date = new Date(utcString);

  if (Number.isNaN(date.getTime())) return 'N/A';

  return new Intl.DateTimeFormat('en-US', {
    timeZone: currentTimeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

/**
 * Get the current period time function
 * @returns {string} period
 */
export const getPeriodCurrentTime = () => {
  const now = new Date();
  const hours = now.getHours();
  const period = hours >= 12 ? 'PM' : 'AM';

  return `${period}`;
};

/**
 * Time to change the hour and minutes
 * @param {any} time - Time string
 * @returns {object} Object containing hours and minutes
 */
export const parseTime = (time: string) => {
  const time12Hour = convertTo12HourFormat(time);
  // Split the string by space to separate time and period
  const [timePart, period] = time12Hour.split(' ');
  // Split the time part by colon to separate hours and minutes
  const [hour, minute] = timePart.split(':');
  return {
    hours: parseInt(hour, 10),
    minutes: parseInt(minute, 10),
    noon: period, // Period will be null for 24-hour format
  };
};

/**
 * Array convert to the group
 * @param {any} array - Array to group
 * @param {any} key - Key to group by
 * @returns {object} Mapped array
 */
export const arrayGroupBy = <T extends Record<string, any>>(
  array: T[],
  key: keyof T,
): Record<string, T[]> => {
  return array.reduce((result: Record<string, T[]>, currentItem) => {
    const outputResult = result;
    const group = currentItem[key] as string; // Ensure it's treated as a string key
    if (!outputResult[group]) {
      outputResult[group] = [];
    }
    outputResult[group].push(currentItem);
    return outputResult;
  }, {});
};

/**
 * Array convert to the date control
 * @param {any} dateString date string
 * @param {any} t translation function
 * @returns {string} relative time
 */
export function getRelativeTime(dateString: string, t?: any): string {
  const now = new Date();
  const past = new Date(dateString);
  const diff = now.getTime() - past.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return t('justNow');
  if (minutes < 60) return `${minutes} ${t('minutesAgo')}`;
  if (hours < 24) return `${hours} ${t('hoursAgo')}`;
  if (days < 7) return `${days} ${t('daysAgo')}`;
  if (weeks < 4) return `${weeks} ${t('weeksAgo')}`;
  if (months < 12) return `${months} ${t('monthsAgo')}`;
  return `${years} ${t('yearsAgo')}`;
}

/**
 * Convert decimal hours to a formatted string
 * @param {any} decimalHours - The decimalHours
 */
export function convertDecimalHoursToTime(decimalHours: number): string {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(hours === 1 ? `${hours} hr` : `${hours} hrs`);
  }

  if (minutes > 0) {
    parts.push(minutes === 1 ? `${minutes} min` : `${minutes} mins`);
  }

  // If both are zero, return "0 min"
  if (parts.length === 0) {
    return '0 min';
  }

  return parts.join(' ');
}

/**
 * getDateRange
 * @param {any} type - The type
 */
export function getDateRange(type: string): { startDate: Dayjs; endDate: Dayjs } {
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');

  switch (type) {
    case 'today':
      return {
        startDate: today,
        endDate: today,
      };
    case 'yesterday':
      return {
        startDate: yesterday,
        endDate: today,
      };
    case 'week':
      return {
        startDate: today.subtract(6, 'day'), // Last 7 days (including today)
        endDate: today,
      };
    case 'month':
      return {
        startDate: today.subtract(29, 'day'), // Last 30 days (including today)
        endDate: today,
      };
    case 'year':
      return {
        startDate: today.subtract(364, 'day'), // Last 365 days (including today)
        endDate: today,
      };
    default:
      return {
        startDate: today,
        endDate: today,
      };
  }
}
/**
 * Function to format minutes in to hours and minutes
 * @param {any} totalMinutes total minutes
 * @returns {string} formatted hours and minutes
 */
export function formatMinutesToHoursAndMinutes(totalMinutes: number): string {
  if (totalMinutes < 0 || !Number.isFinite(totalMinutes)) {
    console.error('Invalid input: totalMinutes must be a non-negative number');
    return '';
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60); // round to avoid decimals

  const hoursString = hours > 0 ? `${hours} hr${hours > 1 ? 's' : ''}` : '';
  const minutesString = minutes > 0 ? `${minutes} min${minutes > 1 ? 's' : ''}` : '';

  return [hoursString, minutesString].filter(Boolean).join(' ');
}

/**
 * Utility function to calculate the number of days between two dates
 * @param {any} date1 date 1
 * @param {any} date2 date 2
 * @returns {number} days
 */
export function getDaysBetween(date1: string, date2: string): number {
  // Convert to Date objects
  const d1 = new Date(`${date1} 00:00:00`);
  const d2 = new Date(`${date2} 23:59:59`);
  // Difference in milliseconds
  const diffInMs = Math.abs(d2.getTime() - d1.getTime());
  // Convert to days
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  return diffInDays;
}
/**
 * Utility function to convert minutes
 * @param {any} minutes minutes
 * @returns {string} converted string
 */
export function convertMinutes(minutes: number) {
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const mins = minutes % 60;

  return `${String(days).padStart(2, '0')}D : ${String(hours).padStart(2, '0')}Hr : ${String(mins).padStart(2, '0')}Min`;
}
